import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { X, Barcode, Camera } from "@phosphor-icons/react";

const REGION_ID = "athlon-barcode-region";

// Mensaje "benigno" que tira html5-qrcode al desmontar en React StrictMode
const BENIGN_STOP_MSG = "Cannot stop, scanner is not running or paused.";

function isBenignStopError(value) {
  const msg = typeof value === "string" ? value : value?.message;
  return typeof msg === "string" && msg.includes("Cannot stop, scanner is not running");
}

export default function BarcodeScanner({ onDetected, onClose }) {
  const scannerRef = useRef(null);
  const startedRef = useRef(false);
  const [status, setStatus] = useState("starting"); // starting | running | error
  const [errorMsg, setErrorMsg] = useState(null);

  // Suprimir el runtime-error overlay que aparece en dev por el mensaje benigno
  // mientras esta pantalla está montada. No afecta otros errores.
  useEffect(() => {
    const onUnhandled = (e) => {
      if (isBenignStopError(e.reason)) {
        e.preventDefault();
      }
    };
    const onError = (e) => {
      if (isBenignStopError(e.error) || isBenignStopError(e.message)) {
        e.preventDefault();
        e.stopImmediatePropagation?.();
        return true;
      }
    };
    window.addEventListener("unhandledrejection", onUnhandled);
    window.addEventListener("error", onError, true);
    return () => {
      window.removeEventListener("unhandledrejection", onUnhandled);
      window.removeEventListener("error", onError, true);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const scanner = new Html5Qrcode(REGION_ID, { verbose: false });
    scannerRef.current = scanner;

    const handleScan = (decodedText) => {
      if (cancelled) return;
      cancelled = true;
      onDetected?.(decodedText);
    };

    const start = async () => {
      try {
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: (w, h) => ({ width: Math.min(w, h) - 40, height: 180 }),
            aspectRatio: 1.5,
          },
          handleScan,
          () => { /* ignore per-frame failures */ }
        );
        startedRef.current = true;
        if (cancelled) {
          // El componente se desmontó mientras arrancábamos — detener ahora que sí está corriendo
          try {
            await scanner.stop();
          } catch { /* benign */ }
          try { scanner.clear(); } catch { /* noop */ }
          return;
        }
        setStatus("running");
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setErrorMsg(
          err?.message?.includes("Permission") || err?.name === "NotAllowedError"
            ? "Permiso de cámara denegado. Habilitalo en el navegador e intentá de nuevo."
            : "No pudimos iniciar la cámara. Verificá los permisos."
        );
      }
    };
    start();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (!s) return;
      const state = typeof s.getState === "function" ? s.getState() : null;
      const running =
        state === Html5QrcodeScannerState.SCANNING ||
        state === Html5QrcodeScannerState.PAUSED;
      if (startedRef.current && running) {
        try {
          Promise.resolve()
            .then(() => s.stop())
            .catch(() => {})
            .finally(() => {
              try { s.clear(); } catch { /* noop */ }
            });
        } catch { /* noop */ }
      } else {
        try { s.clear(); } catch { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" data-testid="barcode-scanner">
      <div className="card-base w-full max-w-md">
        <div className="p-5 border-b border-app flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Barcode size={22} weight="duotone" className="text-primary-app" />
            <h3 className="font-display font-black uppercase text-2xl tracking-tighter">Escanear código</h3>
          </div>
          <button onClick={onClose} className="text-muted hover:text-white" data-testid="close-scanner">
            <X size={22} weight="bold" />
          </button>
        </div>

        <div className="relative bg-black" style={{ aspectRatio: "1 / 1" }}>
          <div id={REGION_ID} className="w-full h-full" />
          {status === "running" && (
            <>
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="border-2 border-[#FF3B30] w-4/5 h-20 relative">
                  <div className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 border-white" />
                  <div className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2 border-white" />
                  <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2 border-white" />
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 border-white" />
                </div>
              </div>
              <div className="absolute bottom-3 inset-x-0 text-center">
                <span className="inline-block bg-black/70 text-white text-[10px] uppercase tracking-widest px-3 py-1 border border-app">
                  Apuntá al código de barras del envase
                </span>
              </div>
            </>
          )}
          {status === "starting" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted text-xs uppercase tracking-widest">
              <Camera size={32} weight="duotone" className="text-primary-app animate-pulse" />
              Iniciando cámara...
            </div>
          )}
          {status === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center p-6">
              <Camera size={32} weight="duotone" className="text-[#FF3B30]" />
              <div className="text-xs text-[#FF3B30]" data-testid="scanner-error">{errorMsg}</div>
              <button onClick={onClose} className="btn-secondary !py-1.5 text-xs">Cerrar</button>
            </div>
          )}
        </div>

        <div className="p-4 text-[11px] text-muted text-center">
          Formatos soportados: EAN-13 · EAN-8 · UPC · Code128
        </div>
      </div>
    </div>
  );
}
